/**
 * Video card — modern, tile-friendly rewrite.
 *
 * Replaces the previous boxed MUI `Card` layout with a flatter,
 * planapus-styled tile that fits next to the rest of the admin UI:
 * 16:9 thumbnail, score/rating overlays pinned to the image corners,
 * two-line title + channel metadata below, a tucked-in "Rate" affordance.
 *
 * Every feature from the prior card survives:
 *   - click opens `/profile/:pid/video/:id`
 *   - aggregate score chip with hex-color bg + rich tooltip
 *   - rating chip (beneficial / neutral / harmful) with icons
 *   - restricted state (chip + red border)
 *   - beneficial state (green accent)
 *   - harmful / unscored dimming
 *   - `recommendedAt` / `timestampLabel` watermark on the thumbnail
 *   - feedback dialog wiring via the existing FeedbackDialog
 *   - loading placeholder + per-video aggregate-score loader
 *
 * What changed (visual only — no behavior regressions intended):
 *   - Outer container is a planapus `cardSx` tile, not an MUI Card.
 *   - Score + rating live on the thumbnail (compact), not below the title.
 *   - Title fonts tightened to 13/400 metadata + 15/600 headline.
 *   - Hover lifts the whole tile rather than swapping the underlying
 *     CardActionArea's overlay.
 *   - Rate button shows on hover (secondary affordance; primary is click
 *     the card) — the tooltip-click still opens feedback for keyboard
 *     users.
 */
import React, {useEffect, useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {Link} from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Tooltip,
  Typography,
} from '@mui/material';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import {
  AgentAggregateScore,
  FeedVideo,
  HistoryVideo,
  ScoreDirection,
  Video,
  VideoRating,
} from '../../types/generated/graphql';
import {getAggregateScoreLoader} from '../../api/graphql_video_aggregate_score';
import {getScoreColor} from '../../utils/scoring';
import {cardSx, pcolors} from '../../theme/planapusTheme';

interface VideoCardProps {
  /**
   * Profile id. Omit (pass undefined) for admin-context renders; the
   * card will route to `/admin/content/video/:id` and skip profile-only
   * features (feedback submit, per-profile links). Previously we used
   * the string sentinel `pid="admin"` for this — removed in favor of
   * an explicit missing prop so callers can't accidentally thread
   * the sentinel into any URL.
   */
  pid?: string;
  video: Video | FeedVideo | HistoryVideo;
  client: ApolloClient<NormalizedCacheObject>;
  feedId?: string;
  recommendedAt?: string;
  timestampLabel?: string;
  /**
   * When set, the aggregate score/rating shown on the card reflects the
   * matching age-band verdict from the finalize row (instead of the
   * universal family-agnostic rating). Used by AdminVideos' age-band
   * filter. `null`/undefined keeps today's behavior.
   */
  childAge?: number | null;
}

export const VideoCard: React.FC<VideoCardProps> = ({
  pid,
  video,
  client,
  recommendedAt,
  timestampLabel = 'Recommended',
  childAge = null,
}) => {
  const aggregateScoreLoader = getAggregateScoreLoader(client, childAge);

  const [aggregateScore, setAggregateScore] = useState<AgentAggregateScore | null>(null);

  useEffect(() => {
    let alive = true;
    // Clear the previous aggregate immediately so flipping the age
    // filter in AdminVideos doesn't show a stale rating during the
    // refetch window. Otherwise the old band's rating ghosts for a
    // beat before the new one lands.
    setAggregateScore(null);
    aggregateScoreLoader
      .load(video.id)
      .then(data => {
        if (alive) setAggregateScore(data);
      })
      .catch(err => {
        // Same defensive swallow the prior card used — a failed
        // aggregate lookup should not break every video tile on the
        // page. We render as "Not yet reviewed" and move on.
        if (alive) console.error('VideoCard: aggregate fetch failed for', video.id, err);
      });
    return () => {
      alive = false;
    };
  }, [video, aggregateScoreLoader, childAge]);

  // Per-video FeedbackDialog removed entirely. Parent feedback
  // now flows through the Inbox / mobile-swipe Yes/No path; the
  // `feedback` collection still receives those swipes for training
  // data. If admin / eval ever needs a per-agent rating UI again,
  // it should live behind an admin gate (out of scope here).

  // Visual states — kept in one place so the thumbnail overlay, the
  // border treatment, and the "not yet reviewed" placeholder all agree.
  const rating = aggregateScore?.rating ?? null;
  const isBeneficial = rating === VideoRating.Beneficial;
  const isHarmful = rating === VideoRating.Harmful;
  const isRestricted = !!aggregateScore?.restricted;
  const isUnscored = !aggregateScore;

  // Pick the border accent first because the outer tile uses it.
  const accent = isRestricted
    ? pcolors.red[500]
    : isBeneficial
      ? pcolors.green[500]
      : isHarmful
        ? pcolors.red[100]
        : pcolors.border;

  return (
    <>
      <Box
        component={Link}
        to={pid ? `/profile/${pid}/video/${video.id}` : `/admin/content/video/${video.id}`}
        sx={{
          ...cardSx,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          textDecoration: 'none',
          color: 'inherit',
          // Subtle accent border per state (restricted red, beneficial
          // green, harmful light red, otherwise default).
          borderColor: accent,
          borderWidth: isRestricted || isBeneficial ? 2 : 1,
          transition: 'transform 120ms ease, box-shadow 120ms ease',
          opacity: isUnscored ? 0.85 : isHarmful ? 0.9 : 1,
          filter: isUnscored ? 'saturate(0.85)' : 'none',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
            // Reveal the Rate button on hover so it doesn't compete
            // with the default primary click (open the video).
            '& [data-role="rate-button"]': {opacity: 1},
          },
        }}
      >
        {/* Thumbnail pane — 16:9, with overlays for timestamp, score,
            rating, and the restricted banner. Uses an aspect-ratio
            container rather than fixed height so the tile reflows
            cleanly across grid sizes. */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            bgcolor: '#000',
          }}
        >
          <Box
            component="img"
            src={video.thumbnails[0]?.url || ''}
            alt={video.title}
            loading="lazy"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />

          {/* Timestamp pill — "Watched 2h ago" / "Recommended 3d ago". */}
          {recommendedAt && (
            <Box
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                bgcolor: 'rgba(0,0,0,0.72)',
                color: 'white',
                px: 0.75,
                py: 0.25,
                borderRadius: 0.75,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 0.2,
              }}
            >
              {timestampLabel === 'Watched' ? `Watched ${recommendedAt}` : recommendedAt}
            </Box>
          )}

          {/* Restricted banner — top-left, strong red so it's the first
              thing the eye lands on in a grid of cards. */}
          {isRestricted && (
            <RestrictedBadge aggregate={aggregateScore} />
          )}

          {/* Score + rating pill cluster — bottom-left. Floats over the
              darkest bit of a typical YouTube thumbnail. */}
          <Box
            sx={{
              position: 'absolute',
              left: 6,
              bottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ScoreOverlay aggregate={aggregateScore} />
          </Box>
        </Box>

        {/* Body — title + channel. Tight line-heights, 2-line clamp. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            p: 1.25,
            flexGrow: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 13.5,
              fontWeight: 600,
              color: pcolors.textPrimary,
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minHeight: `calc(1.35em * 2)`,
            }}
          >
            {video.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 11.5,
              color: pcolors.textSecondary,
              lineHeight: 1.25,
            }}
            noWrap
          >
            {video.author.name}
          </Typography>
        </Box>

        {/* Footer row — secondary affordances. Rate button fades in
            on hover; clicking it stops the propagation so the main
            card-click (open video) isn't triggered. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderTop: `1px solid ${pcolors.borderLight}`,
            minHeight: 36,
          }}
        >
          <Box sx={{flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5}}>
            {aggregateScore ? <RatingInline rating={aggregateScore.rating} /> : (
              <Typography sx={{fontSize: 10.5, color: pcolors.textMuted, fontStyle: 'italic'}}>
                Not yet reviewed
              </Typography>
            )}
          </Box>
          {/* Rate button is profile-scoped — hide on admin renders
              (no pid) since there's no profile to attach feedback to. */}
        </Box>
      </Box>
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Top-left red-banner chip for restricted content, with the rich
 *  "Restricted by: …" tooltip preserved from the previous card. */
function RestrictedBadge({aggregate}: {aggregate: AgentAggregateScore | null}) {
  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <React.Fragment>
          <Typography variant="body2" sx={{fontWeight: 700}}>
            Content restricted
          </Typography>
          {aggregate?.explanation ? (
            <Typography variant="body2">{aggregate.explanation}</Typography>
          ) : (
            <Typography variant="body2">
              This content has been restricted due to policy violations.
              {aggregate?.restrictingAgents && aggregate.restrictingAgents.length > 0 && (
                <>
                  <br />
                  <br />
                  <strong>Restricted by:</strong>{' '}
                  {aggregate.restrictingAgents
                    .map(a => a.specialization.toString().replace(/_/g, ' '))
                    .join(', ')}
                </>
              )}
            </Typography>
          )}
        </React.Fragment>
      }
    >
      <Chip
        icon={<ReportGmailerrorredIcon />}
        label="Restricted"
        size="small"
        sx={{
          position: 'absolute',
          top: 6,
          left: 6,
          bgcolor: pcolors.red[600],
          color: 'white',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
          cursor: 'help',
          // Keep the chip tight; MUI's default height/padding are too
          // bulky for a thumbnail overlay.
          height: 20,
          '& .MuiChip-label': {px: 0.75},
          '& .MuiChip-icon': {fontSize: 13, color: 'white', ml: 0.25},
        }}
        onClick={e => {
          // Prevent the outer anchor from navigating when the user
          // taps the badge (mobile). Tooltip opens instead.
          e.preventDefault();
          e.stopPropagation();
        }}
      />
    </Tooltip>
  );
}

/** Score + verdict overlay pinned to the bottom-left of the thumbnail.
 *  Renders three modes: loading/unscored, restricted (suppressed — the
 *  top-left banner covers it), or a colored score chip. */
function ScoreOverlay({aggregate}: {aggregate: AgentAggregateScore | null}) {
  if (!aggregate) {
    return (
      <Chip
        label="…"
        size="small"
        sx={{
          height: 20,
          fontSize: 10,
          bgcolor: 'rgba(0,0,0,0.55)',
          color: 'white',
          '& .MuiChip-label': {px: 0.75},
        }}
      />
    );
  }
  if (aggregate.restricted) {
    // The RestrictedBadge already occupies the visual prime real
    // estate; showing a numeric score alongside it would muddle the
    // message. Hide for restricted cards.
    return null;
  }
  const bg = Number.isFinite(aggregate.score)
    ? getScoreColor(aggregate.score, ScoreDirection.HigherIsBetter)
    : '#616161';
  const tooltip = (
    <React.Fragment>
      <Typography variant="body2" sx={{fontWeight: 700}}>
        Score {aggregate.score.toFixed(1)} · {aggregate.rating.toString().replace(/_/g, ' ')}
      </Typography>
      {aggregate.explanation && (
        <Typography variant="body2">{aggregate.explanation}</Typography>
      )}
      {!aggregate.explanation && (
        <Typography variant="body2" sx={{fontStyle: 'italic'}}>
          No explanation provided
        </Typography>
      )}
    </React.Fragment>
  );
  return (
    <Tooltip arrow placement="top" title={tooltip}>
      <Chip
        label={aggregate.score.toFixed(0)}
        size="small"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          height: 22,
          minWidth: 22,
          bgcolor: bg,
          color: 'white',
          fontWeight: 800,
          fontSize: 11,
          cursor: 'help',
          border: '1.5px solid rgba(255,255,255,0.9)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
          '& .MuiChip-label': {px: 0.5},
        }}
      />
    </Tooltip>
  );
}

/** Inline rating chip shown in the card footer. Keeps the full rating
 *  label (beneficial / neutral / harmful) visible outside of hover. */
function RatingInline({rating}: {rating: VideoRating}) {
  const tone = ratingTone(rating);
  const icon = ratingIcon(rating);
  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Typography variant="body2">
          {rating === VideoRating.Beneficial
            ? 'This content is beneficial'
            : rating === VideoRating.Harmful
              ? 'This content may be harmful'
              : 'This content is neither particularly beneficial nor harmful'}
        </Typography>
      }
    >
      <Chip
        icon={icon}
        label={rating.toString().toLowerCase().replace(/_/g, ' ')}
        size="small"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          height: 22,
          fontSize: 10.5,
          fontWeight: 600,
          textTransform: 'capitalize',
          bgcolor: tone.bg,
          color: tone.fg,
          cursor: 'help',
          '& .MuiChip-icon': {color: tone.fg, ml: 0.5, fontSize: 13},
          '& .MuiChip-label': {px: 0.75},
        }}
      />
    </Tooltip>
  );
}

function ratingTone(rating: VideoRating): {bg: string; fg: string} {
  switch (rating) {
    case VideoRating.Beneficial:
      return {bg: pcolors.green[50], fg: pcolors.green[700]};
    case VideoRating.Harmful:
      return {bg: pcolors.red[50], fg: pcolors.red[700]};
    case VideoRating.Neutral:
    default:
      return {bg: pcolors.borderLight, fg: pcolors.textSecondary};
  }
}

function ratingIcon(rating: VideoRating) {
  switch (rating) {
    case VideoRating.Beneficial:
      return <CheckCircleIcon />;
    case VideoRating.Harmful:
      return <BlockIcon />;
    case VideoRating.Neutral:
    default:
      return <RemoveCircleOutlineIcon />;
  }
}
